import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import {
  Avatar,
  AvatarFallback,
  Badge,
  Button,
  Separator,
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarInset,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarProvider,
  SidebarTrigger,
} from '@erp/ui';
import {
  Activity,
  BarChart3,
  Bell,
  Database,
  LayoutDashboard,
  LogOut,
  Package,
  ShoppingBag,
  Users,
} from '@/components/icons';
import { ThemeToggle } from '@/components/ThemeToggle';
import { useAuth } from '@/lib/auth-context';
import { isPlatformAdminHost } from '@/lib/platform-admin';
import { usePlatformAdminDashboard } from '@/hooks/use-platform-admin';

const NAV_ITEMS = [
  { label: 'Vue generale', path: '', icon: <LayoutDashboard size={17} /> },
  { label: 'Clients SaaS', path: 'clients', icon: <Users size={17} /> },
  { label: 'Revenus', path: 'revenue', icon: <BarChart3 size={17} /> },
  { label: 'Storefront', path: 'storefront', icon: <ShoppingBag size={17} /> },
  { label: 'ProSpace', path: 'pro', icon: <Package size={17} /> },
  { label: 'Risques', path: 'risks', icon: <Bell size={17} /> },
];

export function PlatformAdminLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const dashboard = usePlatformAdminDashboard();
  const totals = dashboard.data?.totals;
  const adminHost = isPlatformAdminHost();
  const basePath = adminHost ? '/' : '/platform';
  const currentSegment = getCurrentPlatformSegment(location.pathname, adminHost);
  const userInitial = user?.email?.charAt(0).toUpperCase() ?? 'A';

  const handleLogout = async () => {
    await logout();
    navigate(isPlatformAdminHost() ? '/login' : '/platform/login', { replace: true });
  };

  return (
    <SidebarProvider className="min-w-0 overflow-x-hidden">
      <Sidebar variant="inset" collapsible="icon">
        <SidebarHeader>
          <SidebarMenu>
            <SidebarMenuItem>
              <SidebarMenuButton size="lg" asChild>
                <Link to={basePath}>
                  <div className="flex aspect-square size-8 items-center justify-center rounded-[10px] bg-sidebar-primary text-sidebar-primary-foreground">
                    <Database size={18} />
                  </div>
                  <div className="grid min-w-0 flex-1 text-start text-sm leading-tight group-data-[collapsible=icon]:hidden">
                    <span className="truncate font-semibold">Gesty Admin</span>
                    <span className="truncate text-xs text-sidebar-foreground/70">
                      Console plateforme
                    </span>
                  </div>
                </Link>
              </SidebarMenuButton>
            </SidebarMenuItem>
          </SidebarMenu>
        </SidebarHeader>

        <SidebarContent>
          <SidebarGroup>
            <SidebarGroupLabel>Navigation</SidebarGroupLabel>
            <SidebarMenu>
              {NAV_ITEMS.map((item) => {
                const active = currentSegment === item.path;
                return (
                  <SidebarMenuItem key={item.label}>
                    <SidebarMenuButton asChild isActive={active} tooltip={item.label}>
                      <Link to={getPlatformAdminPath(item.path, adminHost)}>
                        {item.icon}
                        <span>{item.label}</span>
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                );
              })}
            </SidebarMenu>
          </SidebarGroup>

          <SidebarGroup>
            <SidebarGroupLabel>Etat global</SidebarGroupLabel>
            <div className="space-y-2 px-2 group-data-[collapsible=icon]:hidden">
              <div className="rounded-lg border bg-sidebar-accent/40 p-3">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-xs text-sidebar-foreground/70">Clients actifs</span>
                  <Badge variant="success">{totals?.activeTenants ?? 0}</Badge>
                </div>
                <div className="mt-2 flex items-center justify-between gap-2">
                  <span className="text-xs text-sidebar-foreground/70">Alertes</span>
                  <Badge variant={(totals?.pastDueTenants ?? 0) > 0 ? 'warning' : 'secondary'}>
                    {(totals?.pastDueTenants ?? 0) + (totals?.trialEndingSoon ?? 0)}
                  </Badge>
                </div>
              </div>
            </div>
          </SidebarGroup>
        </SidebarContent>

        <SidebarFooter>
          <SidebarMenu>
            <SidebarMenuItem>
              <SidebarMenuButton size="lg" className="cursor-default">
                <Avatar className="size-8 rounded-full">
                  <AvatarFallback className="rounded-full text-xs font-semibold">
                    {userInitial}
                  </AvatarFallback>
                </Avatar>
                <div className="grid flex-1 text-start text-sm leading-tight group-data-[collapsible=icon]:hidden">
                  <span className="truncate font-medium">Platform admin</span>
                  <span className="truncate text-xs text-sidebar-foreground/70">
                    {user?.email}
                  </span>
                </div>
              </SidebarMenuButton>
            </SidebarMenuItem>
          </SidebarMenu>
        </SidebarFooter>
      </Sidebar>

      <SidebarInset className="min-w-0 overflow-x-hidden">
        <header className="flex h-14 shrink-0 items-center gap-2 border-b border-border/70 bg-background/85 px-4 backdrop-blur-xl">
          <SidebarTrigger className="-ms-1" />
          <Separator orientation="vertical" className="me-2 data-[orientation=vertical]:h-4" />
          <div className="flex min-w-0 flex-1 items-center gap-2">
            <Activity size={17} className="text-primary" />
            <span className="truncate text-sm font-semibold">Pilotage SaaS Gesty</span>
          </div>
          <ThemeToggle />
          <Button variant="ghost" size="sm" onClick={handleLogout}>
            <LogOut size={15} />
            <span className="hidden sm:inline">Deconnexion</span>
          </Button>
        </header>
        <main className="min-w-0 flex-1 overflow-x-hidden overflow-y-auto p-4 md:p-6">
          <Outlet />
        </main>
      </SidebarInset>
    </SidebarProvider>
  );
}

function getPlatformAdminPath(path: string, adminHost: boolean): string {
  if (adminHost) return path ? `/${path}` : '/';
  return path ? `/platform/${path}` : '/platform';
}

function getCurrentPlatformSegment(pathname: string, adminHost: boolean): string {
  const parts = pathname.split('/').filter(Boolean);
  if (adminHost) return parts[0] ?? '';
  return parts[0] === 'platform' ? parts[1] ?? '' : '';
}
